import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { refreshClientAvatars } from "@/components/ClientAvatar";
import { runAryeoSync } from "@/lib/sync";
import PageHeader from "@/components/PageHeader";
import ProductBoard from "@/components/ProductBoard";
import OrdersTable from "@/components/OrdersTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SORT_OPTIONS } from "@/lib/constants";
import { LayoutGrid, Table2, RefreshCw, Loader2, Search, ArrowDownUp } from "lucide-react";
import { toast } from "sonner";

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [view, setView] = useState("board");
  const [search, setSearch] = useState("");
  const [deliverable, setDeliverable] = useState("all");
  const [sort, setSort] = useState("shoot_asc");
  const [types, setTypes] = useState([]);

  const load = useCallback(async () => {
    try {
      const params = { sort };
      if (search) params.search = search;
      if (deliverable !== "all") params.deliverable = deliverable;
      const { data } = await api.get("/orders", { params });
      setOrders(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  }, [search, deliverable, sort]);

  useEffect(() => {
    api.get("/meta/deliverable-types").then(({ data }) => setTypes(data.types)).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const sync = async () => {
    setSyncing(true);
    try {
      const data = await runAryeoSync();
      refreshClientAvatars();
      const extras = [
        data.created ? `${data.created} new` : null,
        data.archived ? `${data.archived} archived` : null,
        data.removed ? `${data.removed} removed` : null,
      ].filter(Boolean).join(", ");
      toast.success(`Synced ${data.total} orders${extras ? ` (${extras})` : ""}`);
      await load();
    } catch (err) {
      toast.error(apiError(err, "Sync failed"));
    } finally {
      setSyncing(false);
    }
  };

  const changeDeliverableStatus = async (orderId, deliverableId, status) => {
    const prev = orders;
    setOrders((os) => os.map((o) => o.id === orderId
      ? { ...o, deliverables: o.deliverables.map((d) => (d.id === deliverableId || d.aryeo_item_id === deliverableId) ? { ...d, status } : d) }
      : o));
    try {
      await api.patch(`/orders/${orderId}/deliverables/${deliverableId}`, { status });
    } catch (err) {
      setOrders(prev);
      toast.error(apiError(err));
    }
  };

  return (
    <div>
      <PageHeader title="Orders" subtitle={`${orders.length} orders in your pipeline`} testid="orders-header">
        <div className="flex items-center gap-1 rounded-lg border border-border bg-card p-1">
          <button onClick={() => setView("board")} data-testid="view-board-btn"
            className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm transition-colors ${view === "board" ? "bg-primary/15 text-primary" : "text-muted-foreground hover:text-foreground"}`}>
            <LayoutGrid className="h-4 w-4" /> Board
          </button>
          <button onClick={() => setView("table")} data-testid="view-table-btn"
            className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm transition-colors ${view === "table" ? "bg-primary/15 text-primary" : "text-muted-foreground hover:text-foreground"}`}>
            <Table2 className="h-4 w-4" /> Table
          </button>
        </div>
        <Button onClick={sync} disabled={syncing} data-testid="orders-sync-btn" className="gap-2">
          {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          Sync
        </Button>
      </PageHeader>

      <div className="space-y-5 p-6 md:p-8">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1 sm:max-w-xs">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search address or client…"
              className="pl-9" data-testid="orders-search" />
          </div>
          <Select value={deliverable} onValueChange={setDeliverable}>
            <SelectTrigger className="w-[180px]" data-testid="deliverable-filter">
              <SelectValue placeholder="All deliverables" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All deliverables</SelectItem>
              {types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-[200px]" data-testid="orders-sort">
              <ArrowDownUp className="mr-1 h-3.5 w-3.5 text-muted-foreground" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : orders.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border py-16 text-center">
            <p className="text-sm text-muted-foreground">No orders found. Sync from Aryeo to get started.</p>
          </div>
        ) : view === "board" ? (
          <ProductBoard orders={orders} onMove={changeDeliverableStatus} />
        ) : (
          <OrdersTable orders={orders} />
        )}
      </div>
    </div>
  );
}
